import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, ArrowLeft, Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const EASE = [0.16, 1, 0.3, 1];

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-sm text-white placeholder-white/30 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#0066FF] focus:ring-2 focus:ring-[#0066FF]/40";
const labelCls = "block text-[10px] font-semibold tracking-[0.22em] uppercase text-white/45 mb-2";
const errorCls = "text-xs text-red-400 mt-1.5";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
    </svg>
  );
}

function PasswordInput({ id, registration, error, placeholder = "••••••••" }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <div className="relative">
        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" strokeWidth={1.5} />
        <input
          id={id}
          type={show ? "text" : "password"}
          placeholder={placeholder}
          className={`${inputCls} pr-12`}
          {...registration}
        />
        <button
          type="button"
          data-testid={`${id}-toggle`}
          onClick={() => setShow((s) => !s)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 transition-colors duration-200"
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
        </button>
      </div>
      {error && <p className={errorCls}>{error.message}</p>}
    </div>
  );
}

function SubmitButton({ loading, testId, children }) {
  return (
    <motion.button
      type="submit"
      data-testid={testId}
      disabled={loading}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      className="group relative w-full rounded-full py-3.5 text-sm font-semibold text-white bg-gradient-to-r from-[#0066FF] to-[#5E2BFF] shadow-[0_0_24px_rgba(0,102,255,0.35)] hover:shadow-[0_0_40px_rgba(94,43,255,0.5)] transition-shadow duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <>
          {children}
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
        </>
      )}
    </motion.button>
  );
}

function ServerError({ message }) {
  if (!message) return null;
  return (
    <motion.p
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="auth-error-message"
      className="text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-xl px-4 py-3"
    >
      {message}
    </motion.p>
  );
}

export default function AuthCard() {
  const [view, setView] = useState("login"); // login | register | forgot
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const loginForm = useForm({ mode: "onBlur" });
  const registerForm = useForm({ mode: "onBlur" });
  const forgotForm = useForm({ mode: "onBlur" });

  const switchView = (next) => {
    setServerError("");
    setForgotSent(false);
    setView(next);
  };

  const onSuccess = (user, isNew) => {
    setUser(user);
    toast.success(isNew ? "Your Alignexa AI account is ready." : `Welcome back, ${user.name?.split(" ")[0] || "there"}.`);
    navigate("/dashboard", { state: { user } });
  };

  const onLogin = async (values) => {
    setLoading(true);
    setServerError("");
    try {
      const { data } = await api.post("/auth/login", values);
      onSuccess(data, false);
    } catch (e) {
      setServerError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const onRegister = async (values) => {
    setLoading(true);
    setServerError("");
    try {
      const { data } = await api.post("/auth/register", values);
      onSuccess(data, true);
    } catch (e) {
      setServerError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const onForgot = async (values) => {
    setLoading(true);
    setServerError("");
    try {
      await api.post("/auth/forgot-password", values);
      setForgotSent(true);
    } catch (e) {
      setServerError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const titles = {
    login: { heading: "Welcome back", sub: "Sign in to your clinical workspace." },
    register: { heading: "Create your account", sub: "Start aligning care with intelligence." },
    forgot: { heading: "Reset your password", sub: "We'll email you a secure reset link." },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
      className="w-full max-w-md"
      data-testid="auth-card"
    >
      <div className="glass rounded-3xl p-8 sm:p-10">
        {/* Header */}
        <div className="mb-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={view}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35, ease: EASE }}
            >
              <h2 className="font-display text-3xl tracking-[-0.02em] text-white" data-testid="auth-heading">
                {titles[view].heading}
              </h2>
              <p className="text-sm text-white/45 font-light mt-2">{titles[view].sub}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Tabs */}
        {view !== "forgot" && (
          <div className="relative grid grid-cols-2 gap-1 p-1 rounded-full bg-white/5 border border-white/10 mb-8">
            {["login", "register"].map((tab) => (
              <button
                key={tab}
                type="button"
                data-testid={`tab-${tab}`}
                onClick={() => switchView(tab)}
                className={`relative z-10 rounded-full py-2 text-xs font-semibold tracking-[0.15em] uppercase transition-colors duration-300 ${
                  view === tab ? "text-white" : "text-white/40 hover:text-white/70"
                }`}
              >
                {view === tab && (
                  <motion.span
                    layoutId="auth-tab-pill"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    className="absolute inset-0 -z-10 rounded-full bg-gradient-to-r from-[#0066FF]/30 to-[#5E2BFF]/30 border border-white/15"
                  />
                )}
                {tab === "login" ? "Sign in" : "Sign up"}
              </button>
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          {view === "login" && (
            <motion.form
              key="login"
              data-testid="login-form"
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 16 }}
              transition={{ duration: 0.35, ease: EASE }}
              onSubmit={loginForm.handleSubmit(onLogin)}
              className="space-y-5"
            >
              <div>
                <label htmlFor="login-email" className={labelCls}>Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" strokeWidth={1.5} />
                  <input
                    id="login-email"
                    data-testid="email-input"
                    type="email"
                    placeholder="you@clinic.com"
                    className={inputCls}
                    {...loginForm.register("email", { required: "Email is required." })}
                  />
                </div>
                {loginForm.formState.errors.email && (
                  <p className={errorCls}>{loginForm.formState.errors.email.message}</p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="login-password" className="text-[10px] font-semibold tracking-[0.22em] uppercase text-white/45">
                    Password
                  </label>
                  <button
                    type="button"
                    data-testid="forgot-password-link"
                    onClick={() => switchView("forgot")}
                    className="text-xs text-[#00F0FF]/80 hover:text-[#00F0FF] transition-colors duration-200"
                  >
                    Forgot password?
                  </button>
                </div>
                <PasswordInput
                  id="login-password"
                  registration={loginForm.register("password", { required: "Password is required." })}
                  error={loginForm.formState.errors.password}
                />
              </div>
              <ServerError message={serverError} />
              <SubmitButton loading={loading} testId="login-submit-button">Sign in</SubmitButton>
            </motion.form>
          )}

          {view === "register" && (
            <motion.form
              key="register"
              data-testid="register-form"
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.35, ease: EASE }}
              onSubmit={registerForm.handleSubmit(onRegister)}
              className="space-y-5"
            >
              <div>
                <label htmlFor="register-name" className={labelCls}>Full name</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" strokeWidth={1.5} />
                  <input
                    id="register-name"
                    data-testid="name-input"
                    type="text"
                    placeholder="Dr. Ada Lovelace"
                    className={inputCls}
                    {...registerForm.register("name", {
                      required: "Name is required.",
                      minLength: { value: 2, message: "Please enter your full name." },
                    })}
                  />
                </div>
                {registerForm.formState.errors.name && (
                  <p className={errorCls}>{registerForm.formState.errors.name.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="register-email" className={labelCls}>Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" strokeWidth={1.5} />
                  <input
                    id="register-email"
                    data-testid="register-email-input"
                    type="email"
                    placeholder="you@clinic.com"
                    className={inputCls}
                    {...registerForm.register("email", { required: "Email is required." })}
                  />
                </div>
                {registerForm.formState.errors.email && (
                  <p className={errorCls}>{registerForm.formState.errors.email.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="register-password" className={labelCls}>Password</label>
                <PasswordInput
                  id="register-password"
                  registration={registerForm.register("password", {
                    required: "Password is required.",
                    minLength: { value: 8, message: "Use at least 8 characters." },
                  })}
                  error={registerForm.formState.errors.password}
                />
              </div>
              <ServerError message={serverError} />
              <SubmitButton loading={loading} testId="register-submit-button">Create account</SubmitButton>
            </motion.form>
          )}

          {view === "forgot" && (
            <motion.div
              key="forgot"
              data-testid="forgot-form"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.35, ease: EASE }}
            >
              {forgotSent ? (
                <div className="text-center py-4" data-testid="forgot-success-message">
                  <div className="mx-auto w-14 h-14 rounded-2xl glass flex items-center justify-center mb-5">
                    <MailCheck className="w-6 h-6 text-[#00F0FF]" strokeWidth={1.5} />
                  </div>
                  <p className="font-display text-xl text-white tracking-tight">Check your inbox</p>
                  <p className="text-sm text-white/45 font-light mt-2 leading-relaxed">
                    If an account exists for that email, a secure reset link is on its way. It expires in 1 hour.
                  </p>
                </div>
              ) : (
                <form onSubmit={forgotForm.handleSubmit(onForgot)} className="space-y-5">
                  <div>
                    <label htmlFor="forgot-email" className={labelCls}>Email</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" strokeWidth={1.5} />
                      <input
                        id="forgot-email"
                        data-testid="forgot-email-input"
                        type="email"
                        placeholder="you@clinic.com"
                        className={inputCls}
                        {...forgotForm.register("email", { required: "Email is required." })}
                      />
                    </div>
                    {forgotForm.formState.errors.email && (
                      <p className={errorCls}>{forgotForm.formState.errors.email.message}</p>
                    )}
                  </div>
                  <ServerError message={serverError} />
                  <SubmitButton loading={loading} testId="forgot-submit-button">Send reset link</SubmitButton>
                </form>
              )}
              <button
                type="button"
                data-testid="back-to-login-link"
                onClick={() => switchView("login")}
                className="mt-6 flex items-center gap-2 text-xs text-white/50 hover:text-white transition-colors duration-200"
              >
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                Back to sign in
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Google + divider */}
        {view !== "forgot" && (
          <>
            <div className="flex items-center gap-4 my-8">
              <span className="flex-1 h-px bg-white/10" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-white/30">or continue with</span>
              <span className="flex-1 h-px bg-white/10" />
            </div>
            <motion.button
              type="button"
              data-testid="google-login-button"
              onClick={handleGoogleLogin}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              className="w-full rounded-full py-3.5 bg-white text-[#050814] text-sm font-semibold flex items-center justify-center gap-3 hover:shadow-[0_0_32px_rgba(255,255,255,0.25)] transition-shadow duration-300"
            >
              <GoogleIcon />
              Google
            </motion.button>
          </>
        )}
      </div>

      <p className="text-center text-[11px] text-white/30 font-light mt-6 tracking-wide">
        Protected by HIPAA-grade encryption · Alignexa AI
      </p>
    </motion.div>
  );
}
